// Global Integrations Admin Page
// Platform admins manage integrations that are available to all tenants.
// Global integrations appear in every tenant's integration list with a "Global" badge.

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  Textarea,
} from '@teable/ui-lib';
import {
  CheckCircle,
  Edit,
  Globe,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  TestTube,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { mainClient } from '@/utils/api/mainClient';

type IntegrationType =
  | 'email'
  | 'webhook'
  | 'sms'
  | 'slack'
  | 'teams'
  | 'other';

interface GlobalIntegration {
  id: string;
  organisation_id: null;
  integration_type: IntegrationType;
  name: string;
  provider?: string;
  config: Record<string, unknown>;
  is_active: boolean;
  is_default: boolean;
  is_global: true;
  last_tested_at?: string;
  last_test_status?: 'success' | 'failed' | 'unknown';
  created_at: string;
  updated_at: string;
}

interface FormState {
  name: string;
  integration_type: IntegrationType;
  provider: string;
  rawConfig: string;
  is_active: boolean;
  is_default: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  integration_type: 'email',
  provider: '',
  rawConfig: '{}',
  is_active: true,
  is_default: false,
};

const getIcon = (type: string) => {
  switch (type) {
    case 'sms':
      return MessageSquare;
    case 'email':
      return Mail;
    default:
      return Globe;
  }
};

// Plugin metadata from automation service — used to auto-derive integration_type
type PluginMeta = { supported_types: string[] };

export const GlobalIntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<GlobalIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [providerHint, setProviderHint] = useState<string | null>(null); // shows auto-detected type

  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    id: string;
    success: boolean;
    message: string;
  } | null>(null);

  const fetchIntegrations = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/altzero-spaces/integrations/admin/global', {
        headers: { 'x-user-id': await getCurrentUserId() },
      });
      if (!res.ok) throw new Error('Failed to fetch global integrations');
      const json = await res.json();
      setIntegrations(json.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load global integrations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const getCurrentUserId = async (): Promise<string> => {
    const { supabase } = await import('../../utils/supabaseClient');
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.user.id || '';
  };

  // Fetch plugin metadata via main server proxy — automation URL never exposed to frontend
  const fetchPluginMeta = async (providerName: string) => {
    const target = providerName.trim().toLowerCase();
    if (!target) return;
    try {
      const { data: meta } = await mainClient.get<PluginMeta>(
        `/api/altzero-spaces/integrations/plugins/${encodeURIComponent(target)}`
      );
      const derivedType =
        (meta.supported_types?.[0] as IntegrationType) || 'other';
      setForm(f => ({ ...f, integration_type: derivedType }));
      setProviderHint(`Detected type: ${derivedType}`);
    } catch {
      setProviderHint(null);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setProviderHint(null);
    setIsDialogOpen(true);
  };

  const openEdit = (integration: GlobalIntegration) => {
    setEditingId(integration.id);
    setForm({
      name: integration.name,
      integration_type: integration.integration_type,
      provider: integration.provider || '',
      rawConfig: JSON.stringify(integration.config, null, 2),
      is_active: integration.is_active,
      is_default: integration.is_default,
    });
    setFormErrors({});
    setProviderHint(`Type: ${integration.integration_type}`);
    setIsDialogOpen(true);
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.provider.trim()) errors.provider = 'Provider is required';
    try {
      JSON.parse(form.rawConfig);
    } catch {
      errors.rawConfig = 'Config must be valid JSON';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const userId = await getCurrentUserId();
      const config = JSON.parse(form.rawConfig);
      const body = {
        integration_type: form.integration_type,
        name: form.name.trim(),
        provider: form.provider.trim(),
        config,
        is_active: form.is_active,
        is_default: form.is_default,
      };

      const url = editingId
        ? `/api/altzero-spaces/integrations/admin/global/${editingId}`
        : '/api/altzero-spaces/integrations/admin/global';

      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }

      setIsDialogOpen(false);
      fetchIntegrations();
    } catch (err: any) {
      setFormErrors({ submit: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (integration: GlobalIntegration) => {
    if (
      !window.confirm(
        `Delete global integration "${integration.name}"? All tenants using it will lose access.`
      )
    ) {
      return;
    }
    try {
      const userId = await getCurrentUserId();
      const res = await fetch(
        `/api/altzero-spaces/integrations/admin/global/${integration.id}`,
        {
          method: 'DELETE',
          headers: { 'x-user-id': userId },
        }
      );
      if (!res.ok) throw new Error('Failed to delete');
      fetchIntegrations();
    } catch (err: any) {
      alert(`Failed to delete: ${err.message}`);
    }
  };

  const handleTest = async (integration: GlobalIntegration) => {
    setTestingId(integration.id);
    setTestResult(null);
    try {
      const userId = await getCurrentUserId();
      const res = await fetch(
        `/api/altzero-spaces/integrations/admin/global/${integration.id}/test`,
        {
          method: 'POST',
          headers: { 'x-user-id': userId },
        }
      );
      const result = await res.json();
      setTestResult({
        id: integration.id,
        success: result.success,
        message: result.message,
      });
    } catch (err: any) {
      setTestResult({
        id: integration.id,
        success: false,
        message: err.message,
      });
    } finally {
      setTestingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-md bg-red-50 p-4 text-sm text-red-800'>
        {error}
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-xl font-semibold'>Global Integrations</h2>
          <p className='text-sm text-muted-foreground'>
            Platform-managed integrations available to all tenants. Tenants can
            use these without configuring their own credentials.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className='mr-2 h-4 w-4' />
          Add Global Integration
        </Button>
      </div>

      {integrations.length === 0 ? (
        <Card>
          <CardContent className='py-12 text-center'>
            <Globe className='mx-auto mb-3 h-10 w-10 text-muted-foreground/40' />
            <p className='text-muted-foreground'>No global integrations yet.</p>
            <p className='mt-1 text-xs text-muted-foreground'>
              Create one to make it available to all tenants.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
          {integrations.map(integration => {
            const Icon = getIcon(integration.integration_type);
            const isThisBeingTested = testingId === integration.id;
            const thisTestResult =
              testResult?.id === integration.id ? testResult : null;

            return (
              <Card key={integration.id}>
                <CardHeader>
                  <div className='flex items-start justify-between'>
                    <div className='flex items-center space-x-2'>
                      <Icon className='h-5 w-5' />
                      <CardTitle className='text-base'>
                        {integration.name}
                      </CardTitle>
                    </div>
                    <div className='flex flex-wrap gap-1'>
                      <Badge
                        variant='outline'
                        className='text-xs border-blue-400 text-blue-600'
                      >
                        Global
                      </Badge>
                      {integration.is_default && (
                        <Badge variant='default' className='text-xs'>
                          Default
                        </Badge>
                      )}
                      {integration.is_active ? (
                        <Badge variant='default' className='text-xs'>
                          Active
                        </Badge>
                      ) : (
                        <Badge variant='secondary' className='text-xs'>
                          Inactive
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription>
                    {integration.provider || integration.integration_type}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='space-y-2'>
                    {/* Last test status */}
                    {(integration.last_test_status || thisTestResult) && (
                      <div className='flex items-center space-x-2 text-sm'>
                        {(thisTestResult?.success ??
                        integration.last_test_status === 'success') ? (
                          <>
                            <CheckCircle className='h-4 w-4 text-green-600' />
                            <span className='text-green-600'>
                              {thisTestResult?.message || 'Last test: Success'}
                            </span>
                          </>
                        ) : (
                          <>
                            <XCircle className='h-4 w-4 text-red-600' />
                            <span className='text-red-600'>
                              {thisTestResult?.message || 'Last test: Failed'}
                            </span>
                          </>
                        )}
                      </div>
                    )}

                    <div className='flex space-x-2 pt-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => handleTest(integration)}
                        disabled={isThisBeingTested}
                      >
                        {isThisBeingTested ? (
                          <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                        ) : (
                          <TestTube className='mr-1 h-3 w-3' />
                        )}
                        Test
                      </Button>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => openEdit(integration)}
                      >
                        <Edit className='mr-1 h-3 w-3' />
                        Edit
                      </Button>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => handleDelete(integration)}
                      >
                        <Trash2 className='mr-1 h-3 w-3' />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={open => {
          setIsDialogOpen(open);
          if (!open) setEditingId(null);
        }}
      >
        <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? 'Edit Global Integration'
                : 'Create Global Integration'}
            </DialogTitle>
            <DialogDescription>
              This integration will be available to all tenants on the platform.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className='space-y-4 pt-2'>
            {formErrors.submit && (
              <div className='rounded-md bg-red-50 p-3 text-sm text-red-800'>
                {formErrors.submit}
              </div>
            )}

            <div className='space-y-2'>
              <Label htmlFor='gi-name'>Name *</Label>
              <Input
                id='gi-name'
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder='e.g. Platform Resend'
              />
              {formErrors.name && (
                <p className='text-sm text-red-600'>{formErrors.name}</p>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='gi-provider'>Provider *</Label>
              <Input
                id='gi-provider'
                value={form.provider}
                onChange={e => {
                  setForm(f => ({
                    ...f,
                    provider: e.target.value.toLowerCase().trim(),
                  }));
                  setProviderHint(null);
                }}
                onBlur={() => fetchPluginMeta(form.provider)}
                placeholder='e.g. resend, sendgrid, twilio'
              />
              {formErrors.provider && (
                <p className='text-sm text-red-600'>{formErrors.provider}</p>
              )}
              {providerHint && (
                <p className='text-xs text-green-700'>✓ {providerHint}</p>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='gi-config'>Config (JSON) *</Label>
              <Textarea
                id='gi-config'
                value={form.rawConfig}
                onChange={e =>
                  setForm(f => ({ ...f, rawConfig: e.target.value }))
                }
                rows={6}
                placeholder={
                  '{\n  "apiKey": "re_xxx",\n  "fromEmail": "noreply@platform.com"\n}'
                }
                className={
                  formErrors.rawConfig
                    ? 'border-red-500 font-mono text-sm'
                    : 'font-mono text-sm'
                }
              />
              {formErrors.rawConfig && (
                <p className='text-sm text-red-600'>{formErrors.rawConfig}</p>
              )}
              <p className='text-xs text-muted-foreground'>
                Keys must match what the provider plugin expects (see plugin
                SKILL.md).
              </p>
            </div>

            <div className='flex items-center space-x-2'>
              <Switch
                id='gi-active'
                checked={form.is_active}
                onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
              />
              <Label
                htmlFor='gi-active'
                className='cursor-pointer flex items-center gap-1.5'
              >
                Active
                {form.is_active && (
                  <CheckCircle className='h-4 w-4 text-green-600' />
                )}
              </Label>
            </div>

            <div className='flex items-center space-x-2'>
              <Switch
                id='gi-default'
                checked={form.is_default}
                onCheckedChange={v => setForm(f => ({ ...f, is_default: v }))}
              />
              <Label
                htmlFor='gi-default'
                className='cursor-pointer flex items-center gap-1.5'
              >
                Set as default integration for all tenants
                {form.is_default && (
                  <CheckCircle className='h-4 w-4 text-green-600' />
                )}
              </Label>
            </div>

            <div className='flex justify-end space-x-2 pt-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setIsDialogOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Saving...
                  </>
                ) : editingId ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
